import appConfigStoreModule from "@core/@app-config/appConfigStoreModule";
import billingModule from "@/store/billing";
import Vue from "vue";
import Vuex from "vuex";
import createPersistedState from "vuex-persistedstate";
import { getRules } from "@/ability";
import { dataProvider } from "@/@core/http";

Vue.use(Vuex);

export default new Vuex.Store({
  plugins: [
    createPersistedState({
      storage: window.localStorage,
      /**
       * Only these variables (or the whole module variables) will be persisted.
       * https://github.com/robinvdvleuten/vuex-persistedstate
       * paths section
       */
      paths: [
        "api",
        "appConfig",
        "assignedOrganizations",
        "auth",
        "currentlyActiveOrganization",
        "billing.subscription",
        "displayVerifyEmailDisclaimer",
        "displayDripSurveyModal",
        "messages",
        "shallContentShowOverlay",
        "user",
        "billingPlan",
        "paymentDiscounts",
        "previousRoute",
        "table",
      ],
    }),
  ],
  namespaced: true,
  state: {
    shallContentShowOverlay: false,
    // TODO: State needs to be reworked a bit,
    // naming needs to be more consistent
    // and some of the properties are not used
    // and can be removed
    assignedOrganizations: [],
    user: {
      id: "",
      email: "",
      firstName: "",
      lastName: "",
      timezone: "",
      status: "",
      onboarded: null,
      organizations: [],
      verified: true,
      roles: [],
    },
    table: {
      page: {
        activeColumns: [],
      },
      abTest: {
        activeColumns: [],
      },
    },
    currentlyActiveOrganization: {},
    // Only homepage verification disclaimer. Profile page email verification disclaimer is not affected by this state variable.
    displayVerifyEmailDisclaimer: true,
    // Drip survey modal, displayed only to new registered users
    displayDripSurveyModal: false,
    // Billing plan name that was set via url param (coming from inulti sales lander)
    billingPlan: "",
    // Previuos route, partial path
    previousRoute: "",
    // Payment discounts for /billing view
    paymentDiscounts: [],
    // Custom css for tinymce editor
    tinymceCss: "",
    // Skip redirect on logout
    skipLogoutRedirect: false,
  },
  mutations: {
    SET_BILLING_PLAN(state, value) {
      state.billingPlan = value;
    },
    SET_PAYMENT_DISCOUNTS(state, value) {
      state.paymentDiscounts = value;
    },
    SET_DISPLAY_VERIFY_EMAIL_DISCLAIMER(state, value) {
      state.displayVerifyEmailDisclaimer = value;
    },
    SET_DISPLAY_DRIP_SURVEY_MODAL(state, value) {
      state.displayDripSurveyModal = value;
    },
    TOGGLE_CONTENT_OVERLAY(state, value) {
      state.shallContentShowOverlay = value !== undefined ? value : !state.shallContentShowOverlay;
    },
    SET_ASSIGNED_USER_ORGANIZATIONS(state, value) {
      state.assignedOrganizations = value;
    },
    SET_USER_ID(state, value) {
      state.user.id = value;
    },
    SET_USER_FIRST_NAME(state, value) {
      state.user.firstName = value;
    },
    SET_USER_LAST_NAME(state, value) {
      state.user.lastName = value;
    },
    SET_USER_EMAIL(state, value) {
      state.user.email = value;
    },
    SET_USER_PHONE_NUMBER(state, value) {
      state.user.phoneNumber = value || "";
    },
    SET_USER_TIMEZONE(state, value) {
      state.user.timezone = value;
    },
    SET_USER_STATUS(state, value) {
      state.user.status = value;
    },
    // Onboarding finished/hidden variable
    SET_USER_ONBOARDED(state, value) {
      state.user.onboarded = value;
    },
    // Auto close onboarding variable
    SET_USER_ONBOARDING_COMPLETE(state, value) {
      state.user.onboardingComplete = value;
    },
    SET_USER_ORGANIZATIONS(state, value) {
      state.user.organizations = value;
    },
    SET_USER_VERIFIED(state, value) {
      state.user.verified = value;
    },
    SET_USER_ROLES(state, value) {
      state.user.roles = value;
    },
    // To be impersonated user email
    SET_USER_IMPERSONATE(state, value) {
      state.user.impersonate = value;
    },
    // Original user is the user that impersonates
    SET_ORIGINAL_USER(state, value) {
      state.user.originalUser = value;
    },
    /**
     * Currently active (selected) UserOrganization
     * UserOrganization is different than Organization
     * UserOrganization.permissions is an array of user permissions for that specific organization
     * Organization.permissions is all of the permission options of the organization.
     */
    SET_ACTIVE_ORGANIZATION(state, value) {
      state.currentlyActiveOrganization = value;
    },
    SET_PREVIOUS_ROUTE(state, value) {
      state.previousRoute = value;
    },
    SET_TINYMCE_CSS(state, value) {
      state.tinymceCss = value;
    },
    SET_SKIP_LOGOUT_REDIRECT(state, value) {
      state.skipLogoutRedirect = value;
    },
    SET_PAGE_TABLE_COLUMNS(state, value) {
      state.table.page.activeColumns = value;
    },
    SET_AB_TEST_TABLE_COLUMNS(state, value) {
      state.table.abTest.activeColumns = value;
    },
  },
  getters: {
    getCurrentUser: state => {
      return { ...state.user };
    },
    getActiveOrganization: state => state.currentlyActiveOrganization,
    getUserOrganizations: state => state.assignedOrganizations,
    getPreviousRoute: state => state.previousRoute,
  },
  actions: {
    SET_USER_DATA: ({ commit, getters, dispatch }, payload) => {
      commit("SET_ASSIGNED_USER_ORGANIZATIONS", payload?.organizations);
      // If there is no current active organiation set the 1st one
      dispatch("setActiveOrganization", payload?.organizations[0]);
      commit("SET_USER_ID", payload?.id);
      commit("SET_USER_FIRST_NAME", payload?.firstName);
      commit("SET_USER_LAST_NAME", payload?.lastName);
      commit("SET_USER_EMAIL", payload?.email);
      commit("SET_USER_PHONE_NUMBER", payload?.phoneNumber);
      commit("SET_USER_TIMEZONE", payload?.timezone);
      commit("SET_USER_STATUS", payload?.status);
      commit("SET_USER_ONBOARDED", payload?.onboarded);
      commit("SET_USER_ORGANIZATIONS", payload?.organizations);
      commit("SET_USER_VERIFIED", payload?.verified);
      commit("SET_USER_ROLES", payload?.roles);
      dispatch("updateAbilities");
    },

    setActiveOrganization({ commit }, userOrganization) {
      commit("SET_ACTIVE_ORGANIZATION", userOrganization);
      commit("billing/SET_SUBSCRIPTION", userOrganization?.organization?.subscription || null);
    },

    updateAbilities() {
      this._vm.$ability.update(getRules());
    },

    async refreshAndStoreAllUserData({ dispatch }) {
      const { data } = await dataProvider.customHttpCall("GET", `users/current`);
      dispatch("SET_USER_DATA", data);
    },
    async refreshCurrentlyActiveOrganizationData({ commit, state, dispatch }) {
      const { data } = await dataProvider.customHttpCall("GET", `users/current`);
      const activeOrganization = data.organizations.find(userOrganization => {
        return userOrganization["@id"] === state.currentlyActiveOrganization["@id"];
      });

      // If active organization is not found (deleted), refresh user data, including the organizations
      if (!activeOrganization) {
        // Refresh and store all user data
        return dispatch("refreshAndStoreAllUserData");
      }
      commit("SET_ASSIGNED_USER_ORGANIZATIONS", data.organizations);
      commit("SET_ACTIVE_ORGANIZATION", activeOrganization);
    },
  },
  modules: {
    appConfig: appConfigStoreModule,
    billing: billingModule,
  },
});
